import {
  OnErrorHandler,
  TaggingLogger, TaggingOptions, TaggingParser, TaggingPayload,
} from './typings';

const isEventEnabled = (options: TaggingOptions, { eventName, flowType }: TaggingPayload, onError?: OnErrorHandler): boolean => {
  const eventFromConfig = options.events[eventName];

  if (!eventFromConfig) {
    if (onError != null) {
      onError(`Event ${eventName} is not configured`);
    }
    return false;
  }
  const { enabled = true, flowTypes } = eventFromConfig;

  return enabled && (flowTypes == null || flowTypes.some((f) => f === flowType));
};

const selectParser = (options: TaggingOptions, { eventName, flowType }: TaggingPayload): TaggingParser => {
  const { parser } = options.events[eventName];

  if (typeof parser === 'function') {
    return parser;
  }

  return parser[flowType ?? ''] ?? parser.DEFAULT;
};

const applyMiddlewares = (options: TaggingOptions, payload: TaggingPayload, onError?: OnErrorHandler): TaggingPayload => {
  let result = payload;

  if (options.middlewares && options.middlewares.length > 0) {
    options.middlewares.forEach((m) => { result = m(payload, onError); });
  }

  return result;
};

export const configureTagging = (options: TaggingOptions): TaggingLogger => {
  const {
    features,
    handlers: { onPublish, onError },
  } = options;

  return (payload: TaggingPayload) => {
    try {
      if (features.base && isEventEnabled(options, payload, onError)) {
        const result = applyMiddlewares(options, payload, onError);
        const { eventName, flowType, origin } = result;
        const parseEvent = selectParser(options, result);
        onPublish({
          eventName,
          flowType,
          origin,
          data: { ...parseEvent(result, onError) },
        });
      }
    } catch (error) {
      if (onError) {
        onError(`Unexpected error ${error} while handling payload ${payload}`);
      }
    }
  };
};

export * from './typings';
export * from './parsers';
export * as validators from './validators';
